import { API } from 'utils';
import { GW_SERVICES_PREFIXES } from 'constants/gateway';
import { IConstant, TSignalStatus } from 'types';
import { IParams } from '../types';

const { SIGNAL, REQUEST } = GW_SERVICES_PREFIXES;

export type CrystalConnectionStatusDTO = 'connected' | 'failure' | 'new';
export type CrystalDataDTO = {
  kristall_id: string;
  ip_address: string;
  port: number;
  name: string;
  address: string;
  connection_status: CrystalConnectionStatusDTO;
};
export type CrystalListDTO = {
  kristalls: CrystalDataDTO[];
};

export type DirectoryDTO = {
  directory_id: string;
  events: {
    id: number;
    name: string;
  }[];
  houses: {
    id: number;
    address: string;
  }[];
  kristall_id: string;
};

export type CrystalCategoryDTO = IConstant;

/** category - это SignalCategory из constants */
export type EventMappingDTO = {
  kristall_id: string;
  mappings: {
    category: string | null;
    event_type: string;
    mapping_id: string;
  }[];
};

export type EventMappingPayloadDTO = {
  mappings: Omit<EventMappingDTO['mappings'][number], 'mapping_id'>[];
};

/**
 * category - это SignalCategory из constants,
 * routing_mode - это RoutingMode из constants, manual | default.
 * в routing_mode default manual_org_ids - null, в manual - массив id организаций.
 */
export type HouseMappingDTO = {
  kristall_id: string;
  mappings: {
    categories: {
      category: string;
      category_id: string;
      manual_org_ids: string[];
    }[];
    house_raw: string;
    routing_mode: string;
    system_address: string | null;
    system_address_id: string | null;
  }[];
};

export type HouseMappingPayloadDTO = Pick<
  HouseMappingDTO['mappings'][number],
  'house_raw' | 'routing_mode' | 'system_address' | 'system_address_id'
> & {
  map: {
    category: string;
    org_ids: string[] | null;
  }[];
};

/** Список кристаллов (фильтр по connection_status, пагинация) */
export const GetCrystalList = (params: {
  limit: number;
  offset: number;
  connection_status: CrystalConnectionStatusDTO | null;
}): Promise<CrystalListDTO> =>
  API.get(`/${SIGNAL}/kristalls/`, undefined, params);

/** Информация о конкретном кристалле */
export const GetCrystal = (id: string): Promise<CrystalDataDTO> =>
  API.get(`/${SIGNAL}/kristalls/${id}`);

/** Обновление информации о кристалле (имя, адрес, порт) */
export const UpdateCrystal = (
  id: string,
  data: Partial<Omit<CrystalDataDTO, 'kristall_id'>>
): Promise<CrystalDataDTO> =>
  API.gatewayPatch(`/${SIGNAL}/kristalls/`, id, data);

/** Мягкое удаление (деактивация) кристалла */
export const SoftDeleteCrystal = (id: string): Promise<void> =>
  API.gatewayDelete(`/${SIGNAL}/kristalls/`, id);

/** Хартбит — подтверждение работоспособности кристалла 204 */
export const Heartbeat = (id: string): Promise<void> =>
  API.gatewayPost(`/${SIGNAL}/kristalls/${id}/heartbeat`);

export const GetDirectory = (id: string): Promise<DirectoryDTO> =>
  API.get(`/${SIGNAL}/kristalls/${id}/directory`);

/** Получить сопоставления типов событий */
export const GetEventCategoryMappings = (
  id: string
): Promise<EventMappingDTO> =>
  API.get(`/${SIGNAL}/kristalls/${id}/event-category-mappings`);

/** Установить сопоставления типов событий (полная замена) */
export const SetEventCategoryMappings = (
  id: string,
  data: EventMappingPayloadDTO
): Promise<EventMappingDTO> =>
  API.gatewayPost(`/${SIGNAL}/kristalls/${id}/event-category-mappings`, data);

/** Получить правила маршрутизации по адресу + категории */
export const GetHouseCategoryMappings = (
  id: string
): Promise<HouseMappingDTO> =>
  API.get(`/${SIGNAL}/kristalls/${id}/house-mappings`);

/** Установить правила маршрутизации (полная замена) */
export const SetHouseCategoryMappings = (
  id: string,
  house_id: string,
  data: HouseMappingPayloadDTO
): Promise<HouseMappingDTO> =>
  API.gatewayPut(
    `/${SIGNAL}/kristalls/${id}/house-mappings/${house_id}`,
    undefined,
    data
  );

/** Возвращает постраничный список сигналов, доступных сотруднику.
 * Поддерживает фильтрацию по статусу, категории и номеру, а также сортировку по дате события.
 * http://10.2.1.6:8003/api/docs#/%D0%A1%D0%B8%D0%B3%D0%BD%D0%B0%D0%BB%D1%8B/get_list_signal__get */
export const GetSignalsList = (params: IParams) =>
  API.get(`/${REQUEST}/signal/`, undefined, params);

/** Возвращает полные данные сигнала по его ID.
 * 404 — сигнал не найден или недоступен текущему сотруднику.
 * http://10.2.1.6:8003/api/docs#/%D0%A1%D0%B8%D0%B3%D0%BD%D0%B0%D0%BB%D1%8B/get_signal__signal_id___get */
export const GetSignal = (id: string) => API.get(`/${REQUEST}/signal/`, id);

/** Возвращает все записи истории в хронологическом порядке (от старых к новым).
 * Включает смены статусов, изменения полей и создание заявки из сигнала.
 * 404 — сигнал не найден или недоступен текущему сотруднику.
 * http://10.2.1.6:8003/api/docs#/%D0%A1%D0%B8%D0%B3%D0%BD%D0%B0%D0%BB%D1%8B/history_signal__signal_id__history__get */
export const GetSignalHistory = (id: string) =>
  API.get(`/${REQUEST}/signal/${id}/history/`);

/** Изменяет статус сигнала с проверкой допустимых переходов.
 * new → accepted или in_repair
 * in_repair → accepted
 * accepted → переходов нет (финальный статус)
 * http://10.2.1.6:8003/api/docs#/%D0%A1%D0%B8%D0%B3%D0%BD%D0%B0%D0%BB%D1%8B/update_status_signal__signal_id__status__patch */
export const UpdateSignalStatus = (
  id: string,
  data: { new_status: TSignalStatus; reason: string; repair_until?: string }
) => API.gatewayPatch(`/${REQUEST}/signal/${id}/status/`, undefined, data);

/** Создаёт заявку, автоматически заполняя поля из данных сигнала: статус, тип, категорию, подкатегорию, описание и заявителя.
 * Возвращает ID и номер созданной заявки.
 * http://10.2.1.6:8003/api/docs#/%D0%A1%D0%B8%D0%B3%D0%BD%D0%B0%D0%BB%D1%8B/create_request_signal__signal_id__create_request__post */
export const CreateRequestFromSignal = (
  id: string,
  data: { tag: string; house: { _id: string } }
) => API.gatewayPost(`/${REQUEST}/signal/${id}/create-request/`, data);
